import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSocieties,
  addSociety,
  removeSociety,
  updateSocietyAction,
} from "../store/slices/societySlice";
import { checkActiveUser } from "../store/slices/authSlice";
import Swal from "sweetalert2";
import {
  Camera,
  MapPin,
  Building2,
  Pencil,
  Trash2,
  X,
  Plus,
  Eye,
  Map,
  CheckCircle2,
  Image,
  Info,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Loader,
  Settings,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { AnimatePresence, motion } from "framer-motion";
import PricingConfiguration from "./PricingConfiguration";

const CreateSociety = () => {
  const dispatch = useDispatch();
  const { activeUser } = useSelector((state) => state.auth);
  const { societies } = useSelector((state) => state.societies);
  const [societyData, setSocietyData] = useState({
    name: "",
    location: "",
    created_by: "",
  });

  const { theme, gradient } = useSidebarContext();
  const [societyImageFile, setSocietyImageFile] = useState(null);
  const [societyMapFile, setSocietyMapFile] = useState(null);
  const [societyImagePreview, setSocietyImagePreview] = useState("");
  const [societyMapPreview, setSocietyMapPreview] = useState("");

  const [selectedSociety, setSelectedSociety] = useState(null);
  const [isFieldDisabled, setIsFieldDisabled] = useState(false);
  const [viewingMode, setViewingMode] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [formSection, setFormSection] = useState("details");
  const [showSocietyList, setShowSocietyList] = useState(true);
  const [showPricingConfig, setShowPricingConfig] = useState(false);

  const fileInputRef = useRef(null);
  const fileInputRefMapImage = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        const resultAction = await dispatch(checkActiveUser());
        if (resultAction.payload === null) {
          showAuthAlert();
          return;
        }

        await dispatch(fetchSocieties());
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Initialization error:", error);
        showErrorAlert("An error occurred during initialization.");
        setIsInitialLoad(false);
      }
    };

    initialize();
  }, [dispatch]);

  const rows = useMemo(
    () =>
      societies.map((society, index) => ({
        id: society._id || `temp-${index}`,
        name: society.name || "",
        location: society.location || "",
        society_image: society.society_image || "",
        society_map_image: society.society_map_image || "",
        plotCount: society.plots?.length || 0,
        _id: society._id,
        created_by: society.created_by || "",
      })),
    [societies]
  );

  const showAuthAlert = () => {
    Swal.fire({
      icon: "warning",
      title: "Authentication Required",
      text: "Please log in to manage societies.",
      background: `${theme.mui.BgLinear}`,
      color: `#FACC15`,
      confirmButtonColor: `#FBBF24`,
      customClass: {
        popup: `rounded-2xl border ${theme.tailwind.border2}`,
        title: "text-xl font-bold",
        content: "mt-4",
      },
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: message,
      background: `${theme.mui.BgLinear}`,
      color: `#FCA5A5`,
      confirmButtonColor: `#f27474`,
      customClass: {
        popup: `rounded-2xl border ${theme.tailwind.border2}`,
        title: "text-xl font-bold",
        content: "mt-4",
      },
    });
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: "success",
      title: "Success!",
      text: message,
      background: `${theme.mui.BgLinear}`,
      color: `#5EEAD4`,
      confirmButtonColor: `#008000`,
      customClass: {
        popup: `rounded-2xl border ${theme.tailwind.border2}`,
        title: "text-xl font-bold",
        content: "mt-4",
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSocietyData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showErrorAlert("File size exceeds 5MB limit.");
        return;
      }
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        showErrorAlert("Only JPEG, JPG and PNG files are allowed.");
        return;
      }
      if (field === "society_image") {
        setSocietyImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSocietyImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (field === "society_map_image") {
        setSocietyMapFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSocietyMapPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validateForm = () => {
    if (!societyData.name.trim()) {
      showErrorAlert("Society name is required.");
      return false;
    }

    if (!societyData.location.trim()) {
      showErrorAlert("Location is required.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!activeUser || !activeUser.id) {
      showAuthAlert();
      return;
    }

    try {
      setIsCreating(true);

      const formData = new FormData();

      formData.append("name", societyData.name);
      formData.append("location", societyData.location);
      formData.append("created_by", activeUser.id);

      if (societyImageFile) {
        formData.append("society_image", societyImageFile);
      }

      if (societyMapFile) {
        formData.append("society_map_image", societyMapFile);
      }

      if (selectedSociety) {
        formData.append("_id", selectedSociety._id);
      }

      const action = selectedSociety
        ? updateSocietyAction({
            _id: selectedSociety._id,
            data: formData,
          })
        : addSociety(formData);

      const resultAction = await dispatch(action);

      if (resultAction.type.endsWith("/fulfilled")) {
        showSuccessAlert(
          selectedSociety
            ? "Society updated successfully!"
            : "Society created successfully! The request has been submitted for admin review."
        );

        resetForm();
        setIsInitialLoad(false);
        await dispatch(fetchSocieties());

        setTimeout(() => {
          const societyListElement = document.getElementById("societyList");
          if (societyListElement) {
            societyListElement.scrollIntoView({ behavior: "smooth" });
          }
        }, 500);
      } else {
        throw new Error(resultAction.error.message);
      }
    } catch (error) {
      showErrorAlert(`Failed to save society: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (society) => {
    setViewingMode(null);
    setSocietyData({
      name: society.name,
      location: society.location,
      created_by: activeUser?.id || "",
    });

    setSocietyImagePreview(society.society_image || "");
    setSocietyMapPreview(society.society_map_image || "");

    setSocietyImageFile(null);
    setSocietyMapFile(null);

    setSelectedSociety(society);
    setFormSection("details");

    setShowSocietyList(true);

    setTimeout(() => {
      if (formRef.current) {
        window.scrollTo({
          top: formRef.current.offsetTop - 100,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleDelete = (societyId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action will send a deletion request to an administrator. Do you want to proceed?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, request deletion",
      cancelButtonText: "Cancel",
      background: `${theme.mui.BgLinear}`,
      color: `#5EEAD4`,
      confirmButtonColor: `#FF0000`,
      cancelButtonColor: `#64748b`,
      customClass: {
        popup: `rounded-2xl border ${theme.tailwind.border2}`,
        title: "text-xl font-bold",
        content: "mt-4",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeSociety(societyId))
          .then(() => {
            Swal.fire({
              icon: "info",
              title: "Deletion Request Sent",
              text: "Your request to delete this society has been sent to an administrator for approval.",
              background: `${theme.mui.BgLinear}`,
              color: `#5EEAD4`,
              confirmButtonColor: `#CCCC00`,
              customClass: {
                popup: `rounded-2xl border ${theme.tailwind.border2}`,
                title: "text-xl font-bold",
                content: "mt-4",
              },
            });
            dispatch(fetchSocieties());
          })
          .catch((error) => {
            showErrorAlert(
              `Failed to request society deletion: ${error.message}`
            );
          });
      }
    });
  };

  const handleViewSociety = (society, mode) => {
    setViewingMode(mode);
    setSelectedSociety(society);
  };

  const renderViewMode = () => {
    if (!selectedSociety || !viewingMode) return null;

    if (viewingMode === "details") {
      return (
        <motion.div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={`${theme.tailwind.bg14} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-8">
                <h2
                  className={`text-2xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent`}
                >
                  Society Details
                </h2>
                <button
                  onClick={() => setViewingMode(null)}
                  className={`p-2 ${theme.tailwind.bg5} rounded-full ${theme.tailwind.bgHover24} transition-colors hover:rotate-90 transform duration-300`}
                >
                  <X className={`w-5 h-5 ${theme.tailwind.text9}`} />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <div>
                    <h3
                      className={`text-sm font-medium ${theme.tailwind.texting2} mb-1`}
                    >
                      Society Name
                    </h3>
                    <p
                      className={`text-xl font-semibold ${theme.tailwind.text9} ${theme.tailwind.bg3} p-3 rounded-lg`}
                    >
                      {selectedSociety.name}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`text-sm font-medium ${theme.tailwind.texting2} mb-1`}
                    >
                      Location
                    </h3>
                    <p
                      className={`text-xl ${theme.tailwind.text9} flex items-center gap-2 ${theme.tailwind.bg3} p-3 rounded-lg`}
                    >
                      <MapPin className="w-5 h-5 flex-shrink-0" />
                      {selectedSociety.location}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`text-sm font-medium ${theme.tailwind.texting2} mb-1`}
                    >
                      Total Plots
                    </h3>
                    <p
                      className={`text-xl font-semibold ${theme.tailwind.text9} ${theme.tailwind.bg3} p-3 rounded-lg flex items-center`}
                    >
                      <Building2
                        className={`w-5 h-5 mr-2 ${theme.tailwind.text}`}
                      />
                      {selectedSociety.plotCount || "0"}
                      <span
                        className={`${theme.tailwind.texting2} text-sm ml-2`}
                      >
                        plots available
                      </span>
                    </p>
                  </div>

                  {selectedSociety.created_by && (
                    <div>
                      <h3
                        className={`text-sm font-medium ${theme.tailwind.texting2} mb-1`}
                      >
                        Created By
                      </h3>
                      <p
                        className={`text-lg ${theme.tailwind.text9} ${theme.tailwind.bg3} p-3 rounded-lg`}
                      >
                        {selectedSociety.created_by.username || "Unknown"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-4">
                  <div
                    className={`${theme.tailwind.bg17} rounded-xl overflow-hidden h-40 flex items-center justify-center`}
                  >
                    {selectedSociety.society_image ? (
                      <img
                        src={selectedSociety.society_image}
                        alt={selectedSociety.name}
                        className="w-full h-full object-cover"
                        onClick={() =>
                          handleViewSociety(selectedSociety, "image")
                        }
                      />
                    ) : (
                      <div className="text-center p-6">
                        <Image
                          className={`w-12 h-12 mx-auto mb-2 ${theme.tailwind.text18}`}
                        />
                        <p className={`${theme.tailwind.texting2} text-sm`}>
                          No society image
                        </p>
                      </div>
                    )}
                  </div>

                  <div
                    className={`${theme.tailwind.bg17} rounded-xl overflow-hidden h-40 flex items-center justify-center`}
                  >
                    {selectedSociety.society_map_image ? (
                      <img
                        src={selectedSociety.society_map_image}
                        alt={`${selectedSociety.name} Map`}
                        className="w-full h-full object-cover"
                        onClick={() =>
                          handleViewSociety(selectedSociety, "map")
                        }
                      />
                    ) : (
                      <div className="text-center p-6">
                        <Map
                          className={`w-12 h-12 mx-auto mb-2 ${theme.tailwind.text18}`}
                        />
                        <p className={`${theme.tailwind.texting2} text-sm`}>
                          No map available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button
                  onClick={() => handleEdit(selectedSociety)}
                  className={`flex-1 py-3 px-6 ${theme.tailwind.bgr27} ${theme.tailwind.text9} rounded-xl ${theme.tailwind.groupHoverG8} transition-all duration-300 flex items-center justify-center gap-2 font-medium`}
                >
                  <Pencil className="w-5 h-5" />
                  Edit Society
                </button>
                <button
                  onClick={() => setShowPricingConfig(true)}
                  className={`flex-1 py-3 px-6 ${theme.tailwind.bg32} ${theme.tailwind.text3} rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 font-medium`}
                >
                  <Settings className="w-5 h-5" />
                  Configure Pricing
                </button>
                <button
                  onClick={() => handleDelete(selectedSociety._id)}
                  className="flex-1 py-3 px-6 bg-gradient-to-r from-red-500/20 to-red-400/20 text-red-400 rounded-xl hover:from-red-500/30 hover:to-red-400/30 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                >
                  <Trash2 className="w-5 h-5" />
                  Request Deletion
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      );
    }

    if (viewingMode === "image" || viewingMode === "map") {
      const imageSrc =
        viewingMode === "image"
          ? selectedSociety.society_image || "/default_placeholder.jpg"
          : selectedSociety.society_map_image || null;

      const title = viewingMode === "image" ? "Society Image" : "Society Map";

      return (
        <motion.div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative max-w-5xl w-full lg:mt-16"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="absolute -top-16 w-full flex justify-between items-center">
              <button
                onClick={() => setViewingMode(null)}
                className={`flex items-center gap-2 p-2 ${theme.tailwind.bg55} rounded-xl ${theme.tailwind.bgHover17} transition-colors ${theme.tailwind.text9}`}
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
              <button
                onClick={() => setViewingMode(null)}
                className={`p-3 ${theme.tailwind.bg55} rounded-full ${theme.tailwind.bgHover17} transition-colors hover:rotate-90 transform duration-300`}
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div
              className={`bg-slate-900/80 p-3 rounded-2xl backdrop-blur-sm shadow-2xl`}
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={`${selectedSociety.name} ${title}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
                />
              ) : (
                <div
                  className={`h-[50vh] flex items-center justify-center ${theme.tailwind.bg2} rounded-xl`}
                >
                  <div className="text-center p-10">
                    <Map
                      className={`w-24 h-24 mx-auto mb-6 ${theme.tailwind.text18} opacity-40`}
                    />
                    <p className={`text-xl ${theme.tailwind.texting2}`}>
                      No map image available for this society
                    </p>
                  </div>
                </div>
              )}
            </div>

            <motion.h3
              className="text-center text-white mt-6 font-medium text-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {selectedSociety.name} - {title}
            </motion.h3>
          </motion.div>
        </motion.div>
      );
    }

    return null;
  };

  const resetForm = () => {
    setSocietyData({
      name: "",
      location: "",
      created_by: "",
    });
    setSocietyImageFile(null);
    setSocietyMapFile(null);
    setSocietyImagePreview("");
    setSocietyMapPreview("");
    setSelectedSociety(null);
    setIsFieldDisabled(false);
    setFormSection("details");
  };

  return (
    <div
      className={`${gradient} py-12 px-4 sm:px-6 lg:px-8 rounded-3xl shadow-[0_10px_50px_rgba(8,_112,_184,_0.3)]`}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className={`${theme.tailwind.bg34} backdrop-blur-xl rounded-3xl shadow-xl p-6 md:p-10 border ${theme.tailwind.border2} relative overflow-hidden`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Background decorative elements */}
          <div
            className={`absolute -top-60 -right-60 w-96 h-96 ${theme.tailwind.bg58} rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute -bottom-60 -left-60 w-96 h-96 ${theme.tailwind.bgEx} rounded-full blur-3xl`}
          ></div>

          {/* Content wrapper for z-index control */}
          <div className="relative z-10">
            {societies.length === 0 && !selectedSociety && (
              <motion.div
                className="text-center py-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3, type: "spring" }}
                >
                  <Building2
                    className={`w-28 h-28 mx-auto mb-6 ${theme.tailwind.text9} opacity-80`}
                  />
                </motion.div>
                <h2
                  className={`text-4xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent mb-4`}
                >
                  Welcome! Create Your First Society
                </h2>
                <p
                  className={`${theme.tailwind.texting2} max-w-md mx-auto mb-8 text-lg`}
                >
                  It looks like you haven't created any societies yet. Get
                  started by filling out the form below to create your first
                  society.
                </p>
              </motion.div>
            )}

            {/* Form Header */}
            <motion.div
              className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1
                className={`text-3xl md:text-4xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent flex items-center gap-3`}
              >
                <Building2 className={`w-10 h-10 ${theme.tailwind.text9}`} />
                {selectedSociety ? "Edit Society" : "Create New Society"}
              </h1>

              {selectedSociety && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`group flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.hover1} transition-colors duration-300`}
                >
                  <ArrowLeft className="w-4 h-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
                  <span>Cancel and create new</span>
                </button>
              )}
            </motion.div>

            {/* Tab Navigation */}
            {!isInitialLoad && (
              <div className="mb-8">
                <div className={`flex border-b ${theme.tailwind.border2}`}>
                  <button
                    type="button"
                    onClick={() => setFormSection("details")}
                    className={`px-5 py-4 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                      formSection === "details"
                        ? `${theme.tailwind.border25} ${theme.tailwind.text9}`
                        : `border-transparent ${theme.tailwind.texting2} ${theme.tailwind.hover2} ${theme.tailwind.hoverB6}`
                    }`}
                  >
                    <Info className="w-4 h-4" />
                    Basic Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormSection("images")}
                    className={`px-5 py-4 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                      formSection === "images"
                        ? `${theme.tailwind.border25} ${theme.tailwind.text9}`
                        : `border-transparent ${theme.tailwind.texting2} ${theme.tailwind.hover2} ${theme.tailwind.hoverB6}`
                    }`}
                  >
                    <Image className="w-4 h-4" />
                    Images
                  </button>
                </div>
              </div>
            )}

            {/* Society Form */}
            <form onSubmit={handleSubmit} ref={formRef} className="space-y-8">
              <AnimatePresence mode="wait">
                {formSection === "details" && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label
                          className={`${theme.tailwind.text9} text-sm font-medium flex items-center gap-2`}
                        >
                          <Building2 className="w-4 h-4" />
                          Society Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={societyData.name}
                          onChange={handleInputChange}
                          className={`w-full ${theme.tailwind.bg31} border ${theme.tailwind.border19} rounded-xl px-4 py-3 ${theme.tailwind.text9} ${theme.tailwind.placeHolder4} focus:ring-2 ${theme.tailwind.focusRing6} ${theme.tailwind.focus7} transition-all duration-300`}
                          placeholder="Enter society name"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          className={`${theme.tailwind.text9} text-sm font-medium flex items-center gap-2`}
                        >
                          <MapPin className="w-4 h-4" />
                          Location
                        </label>
                        <div className="relative">
                          <MapPin
                            className={`absolute left-3 top-3.5 w-5 h-5 ${theme.tailwind.text18}`}
                          />
                          <input
                            type="text"
                            name="location"
                            value={societyData.location}
                            onChange={handleInputChange}
                            className={`w-full ${theme.tailwind.bg31} border ${theme.tailwind.border19} rounded-xl pl-10 pr-4 py-3 ${theme.tailwind.text9} ${theme.tailwind.placeHolder4} focus:ring-2 ${theme.tailwind.focusRing6} ${theme.tailwind.focus7} transition-all duration-300`}
                            placeholder="Enter location"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <div></div> {/* Empty div for flex spacing */}
                      <button
                        type="button"
                        onClick={() => setFormSection("images")}
                        className={`px-6 py-3 ${theme.tailwind.bg11} ${theme.tailwind.text9} rounded-xl ${theme.tailwind.bgHover26} transition-all duration-300 flex items-center gap-2 font-medium`}
                      >
                        Continue to Images
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {formSection === "images" && (
                  <motion.div
                    key="images"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <label
                          className={`${theme.tailwind.text9} text-sm font-medium flex items-center gap-2`}
                        >
                          <Camera className="w-4 h-4" />
                          Society Image
                        </label>
                        <div
                          className={`border-2 border-dashed rounded-xl p-6 text-center ${
                            societyImagePreview
                              ? `${theme.tailwind.border29} ${theme.tailwind.bg59}`
                              : `${theme.tailwind.border19} ${theme.tailwind.bg2} ${theme.tailwind.bgHover14} ${theme.tailwind.hoverB7}`
                          } transition-all duration-300 cursor-pointer`}
                          onClick={() => fileInputRef.current.click()}
                        >
                          {societyImagePreview ? (
                            <div className="relative group">
                              <img
                                src={societyImagePreview}
                                alt="Society Preview"
                                className="mx-auto max-h-48 rounded-lg"
                              />
                              <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <p className="text-white text-sm font-medium">
                                  Click to change
                                </p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Camera
                                className={`w-12 h-12 mx-auto mb-4 ${theme.tailwind.text18}`}
                              />
                              <p className={`${theme.tailwind.texting2} mb-2`}>
                                Upload society image
                              </p>
                              <p className={`${theme.tailwind.text18} text-sm`}>
                                Click to browse or drag & drop
                              </p>
                            </>
                          )}
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) =>
                              handleFileUpload(e, "society_image")
                            }
                            className="hidden"
                            accept=".jpg,.jpeg,.png"
                          />
                        </div>
                        <p className={`text-xs ${theme.tailwind.text18}`}>
                          Maximum file size: 5MB. Supported formats: JPG, JPEG,
                          PNG
                        </p>
                      </div>

                      <div className="space-y-4">
                        <label
                          className={`${theme.tailwind.text9} text-sm font-medium flex items-center gap-2`}
                        >
                          <Map className="w-4 h-4" />
                          Society Map (Optional)
                        </label>
                        <div
                          className={`border-2 border-dashed rounded-xl p-6 text-center ${
                            societyMapPreview
                              ? `${theme.tailwind.border29} ${theme.tailwind.bg59}`
                              : `${theme.tailwind.border19} ${theme.tailwind.bg2} ${theme.tailwind.bgHover14} ${theme.tailwind.hoverB7}`
                          } transition-all duration-300 cursor-pointer`}
                          onClick={() => fileInputRefMapImage.current.click()}
                        >
                          {societyMapPreview ? (
                            <div className="relative group">
                              <img
                                src={societyMapPreview}
                                alt="Map Preview"
                                className="mx-auto max-h-48 rounded-lg"
                              />
                              <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <p className="text-white text-sm font-medium">
                                  Click to change
                                </p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Map
                                className={`w-12 h-12 mx-auto mb-4 ${theme.tailwind.text18}`}
                              />
                              <p className={`${theme.tailwind.texting2} mb-2`}>
                                Upload society map
                              </p>
                              <p className={`${theme.tailwind.text18} text-sm`}>
                                Click to browse or drag & drop
                              </p>
                            </>
                          )}
                          <input
                            type="file"
                            ref={fileInputRefMapImage}
                            onChange={(e) =>
                              handleFileUpload(e, "society_map_image")
                            }
                            className="hidden"
                            accept=".jpg,.jpeg,.png"
                          />
                        </div>
                        <p className={`text-xs ${theme.tailwind.text18}`}>
                          Maximum file size: 5MB. Supported formats: JPG, JPEG,
                          PNG
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setFormSection("details")}
                        className={`px-6 py-3 ${theme.tailwind.bg3} text-slate-300 rounded-xl ${theme.tailwind.bgHover31} transition-all duration-300 flex items-center gap-2 justify-center font-medium`}
                      >
                        <ChevronUp className="w-4 h-4" />
                        Back to Details
                      </button>
                      <button
                        type="submit"
                        disabled={isCreating}
                        className={`px-8 py-3 ${
                          theme.tailwind.bgr19
                        } text-slate-900 rounded-xl ${
                          theme.tailwind.groupHoverG9
                        } transition-all duration-300 flex items-center gap-2 justify-center font-medium ${
                          isCreating
                            ? "opacity-70 cursor-not-allowed"
                            : "hover:shadow-lg hover:shadow-teal-500/20"
                        }`}
                      >
                        {isCreating ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            {selectedSociety ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            {selectedSociety ? (
                              <>
                                <CheckCircle2 className="w-5 h-5" />
                                Update Society
                              </>
                            ) : (
                              <>
                                <Plus className="w-5 h-5" />
                                Create Society
                              </>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            {/* Toggle Society List Button */}
            {societies.length > 0 && (
              <div className="mt-16 mb-8 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setShowSocietyList(!showSocietyList)}
                  className={`flex items-center gap-2 ${theme.tailwind.text9} ${theme.tailwind.hover3} transition-colors`}
                >
                  {showSocietyList ? (
                    <>
                      <ChevronUp className="w-5 h-5" />
                      <span>Hide Society List</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-5 h-5" />
                      <span>Show Society List</span>
                    </>
                  )}
                </button>
                <span className={`${theme.tailwind.texting2} text-sm`}>
                  {societies.length}{" "}
                  {societies.length === 1 ? "society" : "societies"} found
                </span>
              </div>
            )}

            {/* Societies List */}
            {showSocietyList && societies.length > 0 && (
              <motion.div
                id="societyList"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8"
              >
                <h2
                  className={`text-2xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent mb-6 flex items-center gap-2`}
                >
                  <Building2 className={`w-6 h-6 ${theme.tailwind.text9}`} />
                  Your Societies
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {rows.map((society) => (
                    <motion.div
                      key={society.id}
                      className={`${theme.tailwind.bg2} border ${theme.tailwind.border2} rounded-xl overflow-hidden shadow-lg ${theme.tailwind.shadowhover8} transition-all duration-300 hover:-translate-y-1`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * (rows.indexOf(society) % 5) }}
                    >
                      <div
                        className={`h-40 ${theme.tailwind.bg3} relative overflow-hidden`}
                      >
                        {society.society_image ? (
                          <img
                            src={society.society_image}
                            alt={society.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Building2
                              className={`w-12 h-12 ${theme.tailwind.text18}`}
                            />
                          </div>
                        )}
                        <div
                          className={`absolute inset-x-0 bottom-0 h-20 ${theme.tailwind.BgTop}`}
                        ></div>
                      </div>

                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3
                            className={`text-xl font-semibold ${theme.tailwind.text9} truncate`}
                          >
                            {society.name}
                          </h3>
                          <div
                            className={`flex items-center gap-1 ${theme.tailwind.text} ${theme.tailwind.bg58} px-2 py-1 rounded-full text-xs`}
                          >
                            <Building2 className="w-3 h-3" />
                            <span>{society.plotCount || 0} plots</span>
                          </div>
                        </div>

                        <div
                          className={`flex items-center gap-2 mb-4 ${theme.tailwind.texting2}`}
                        >
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{society.location}</span>
                        </div>

                        <div className="flex justify-between mt-6">
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                handleViewSociety(society, "details")
                              }
                              className={`p-2 ${theme.tailwind.bg48} rounded-lg ${theme.tailwind.bgHover31} ${theme.tailwind.text9} transition-colors`}
                              title="View Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(society)}
                              className={`p-2 ${theme.tailwind.bg48} rounded-lg ${theme.tailwind.bgHover31} ${theme.tailwind.text9} transition-colors`}
                              title="Edit Society"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(society._id)}
                              className={`p-2 ${theme.tailwind.bg48} rounded-lg ${theme.tailwind.bgHover31} text-red-400 transition-colors`}
                              title="Delete Society"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          {society.society_map_image && (
                            <button
                              onClick={() => handleViewSociety(society, "map")}
                              className={`p-2 ${theme.tailwind.bg48} rounded-lg ${theme.tailwind.bgHover31} ${theme.tailwind.text9} transition-colors`}
                              title="View Map"
                            >
                              <Map className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Full-screen Modal */}
      <AnimatePresence>{renderViewMode()}</AnimatePresence>

      {/* Pricing Configuration Modal */}
      {showPricingConfig && selectedSociety && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] overflow-y-auto m-4">
            <PricingConfiguration
              society={selectedSociety}
              onSave={(pricing) => {
                setShowPricingConfig(false);
                showSuccessAlert("Pricing configuration updated successfully!");
              }}
              onClose={() => setShowPricingConfig(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateSociety;
